import { useMemo, useState, useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export function BirthdayInput({ 
  value, 
  onChange, 
  onBlur,
  onValidationChange,
  required = false,
  disabled = false,
  fullWidth = true,
  label = "Birthday",
  minAge = 18,
  ...props 
}) {
  const [touched, setTouched] = useState(false);

  const maxDate = useMemo(() => dayjs().subtract(minAge, "year"), [minAge]);
  const minDate = useMemo(() => dayjs("1900-01-01"), []);

  const dayjsValue = useMemo(() => {
    if (!value) return null;
    
    if (dayjs.isDayjs(value)) return value;
    
    const formats = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'];
    
    for (const format of formats) {
      const parsed = dayjs(value, format, true);
      if (parsed.isValid()) return parsed;
    }
    
    const fallback = dayjs(value);
    return fallback.isValid() ? fallback : null;
  }, [value]);

  const error = useMemo(() => {
    if (!touched || disabled) return "";
    
    if (required && !dayjsValue) return `${label} is required.`;
    if (value && !dayjsValue) return "Enter a valid date.";
    if (dayjsValue && dayjsValue.isAfter(maxDate, "day")) {
      return `You must be at least ${minAge} years old.`;
    }
    
    return "";
  }, [dayjsValue, touched, disabled, required, label, maxDate, minAge, value]);

  const isValid = useMemo(() => {
    if (disabled) return true;
    if (required && !dayjsValue) return false;
    if (value && !dayjsValue) return false;
    if (dayjsValue && dayjsValue.isAfter(maxDate, "day")) return false;
    return true;
  }, [dayjsValue, disabled, required, maxDate, value]);

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  const handleChange = (newValue) => {
    onChange?.(newValue);
  };

  const handleClose = () => {
    setTouched(true);
    onBlur?.(dayjsValue);
  };

  return (
    <DatePicker
      label={label}
      value={dayjsValue}
      onChange={handleChange}
      onClose={handleClose}
      maxDate={maxDate}
      minDate={minDate}
      disabled={disabled}
      views={["year", "month", "day"]}
      openTo="year"
      format="DD/MM/YYYY"
      slotProps={{
        textField: {
          required,
          fullWidth,
          error: Boolean(error),
          helperText: error,
          onBlur: () => setTouched(true),
        },
      }}
      {...props}
    />
  );
}